import { AppLanguage, getLanguageStrings } from "internationalisation";
import { assertNever } from "../helper";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { WebPlatform } from "../types";
import { generateExternalSurveyTask, generateUploadsTask, generateWebPlatformTask } from "../tasks";

export const MCSA0525CollGroupIds = [
	"MCSA-443-0525-GROUP1-FACEBOOK",
	"MCSA-444-0525-GROUP2-FACEBOOK",
	"MCSA-445-0525-GROUP1-YOUTUBE",
	"MCSA-446-0525-GROUP2-YOUTUBE",
	"MCSA-447-0525-GROUP1-TIKTOK",
	"MCSA-448-0525-GROUP2-TIKTOK",
	"MCSA-449-0525-GROUP1-INSTAGRAM",
	"MCSA-450-0525-GROUP2-INSTAGRAM",
	"MCSA-451-0525-GROUP1-YAHOO",
	"MCSA-452-0525-GROUP2-YAHOO"
] as const;

export type MCSA0525CollGroupId = typeof MCSA0525CollGroupIds[number];

type MCSA0525CollConfig = {
	groupId: MCSA0525CollGroupId,
	metaFieldNames: ReadonlyArray<string>
}

export const MCSA0525CollWebPlatformIds = [
	"MCSA-0525-GROUP1-FACEBOOK",
	"MCSA-0525-GROUP2-FACEBOOK",
	"MCSA-0525-GROUP1-YOUTUBE",
	"MCSA-0525-GROUP2-YOUTUBE",
	"MCSA-0525-GROUP1-TIKTOK",
	"MCSA-0525-GROUP2-TIKTOK",
	"MCSA-0525-GROUP1-INSTAGRAM",
	"MCSA-0525-GROUP2-INSTAGRAM",
	"MCSA-0525-GROUP1-YAHOO",
	"MCSA-0525-GROUP2-YAHOO"
] as const;

export type MCSA0525CollWebPlatformId = typeof MCSA0525CollWebPlatformIds[number];

const getTaskLengthMinutes = (webPlatformId: MCSA0525CollWebPlatformId): string => {
	switch(webPlatformId) {
		case "MCSA-0525-GROUP1-FACEBOOK":
		case "MCSA-0525-GROUP2-FACEBOOK":
		case "MCSA-0525-GROUP1-INSTAGRAM":
		case "MCSA-0525-GROUP2-INSTAGRAM":
		case "MCSA-0525-GROUP1-YAHOO":
		case "MCSA-0525-GROUP2-YAHOO":
			return "7";
		
		case "MCSA-0525-GROUP2-YOUTUBE":
		case "MCSA-0525-GROUP1-YOUTUBE":
		case "MCSA-0525-GROUP1-TIKTOK":
		case "MCSA-0525-GROUP2-TIKTOK":
			return "10";
		
		default:
			assertNever(webPlatformId);
	}
}

const getWebPlatformConfigUrl = (webPlatformId: MCSA0525CollWebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/MCSA-0525/${webPlatformId}.json`
}

const getWebPlatformId = (groupId: MCSA0525CollGroupId): MCSA0525CollWebPlatformId => {
	switch (groupId) {
		case "MCSA-443-0525-GROUP1-FACEBOOK":
			return "MCSA-0525-GROUP1-FACEBOOK";

		case "MCSA-444-0525-GROUP2-FACEBOOK":
			return "MCSA-0525-GROUP2-FACEBOOK";
		
		case "MCSA-445-0525-GROUP1-YOUTUBE":
			return "MCSA-0525-GROUP1-YOUTUBE";
		
		case "MCSA-446-0525-GROUP2-YOUTUBE":
			return "MCSA-0525-GROUP2-YOUTUBE";

		case "MCSA-447-0525-GROUP1-TIKTOK":
			return "MCSA-0525-GROUP1-TIKTOK";

		case "MCSA-448-0525-GROUP2-TIKTOK":
			return "MCSA-0525-GROUP2-TIKTOK";

		case "MCSA-449-0525-GROUP1-INSTAGRAM":
			return "MCSA-0525-GROUP1-INSTAGRAM";

		case "MCSA-450-0525-GROUP2-INSTAGRAM":
			return "MCSA-0525-GROUP2-INSTAGRAM";

		case "MCSA-451-0525-GROUP1-YAHOO":
			return "MCSA-0525-GROUP1-YAHOO";
		
		case "MCSA-452-0525-GROUP2-YAHOO":
			return "MCSA-0525-GROUP2-YAHOO";
	}
}

const getWebPlatform = (groupId: MCSA0525CollGroupId): WebPlatform => {
	switch(groupId) {
		case "MCSA-443-0525-GROUP1-FACEBOOK":
		case "MCSA-444-0525-GROUP2-FACEBOOK":
			return "Facebook";
		
		case "MCSA-445-0525-GROUP1-YOUTUBE":
		case "MCSA-446-0525-GROUP2-YOUTUBE":
			return "Youtube";

		case "MCSA-447-0525-GROUP1-TIKTOK":
		case "MCSA-448-0525-GROUP2-TIKTOK":
			return "TikTok";

		case "MCSA-449-0525-GROUP1-INSTAGRAM":
		case "MCSA-450-0525-GROUP2-INSTAGRAM":
			return "Instagram";

		case "MCSA-451-0525-GROUP1-YAHOO":
		case "MCSA-452-0525-GROUP2-YAHOO":
			return "Yahoo";

		default:
			assertNever(groupId);
	}
}

const getSurveyUrl = (groupId: MCSA0525CollGroupId): string => {
	switch(groupId) {
		case "MCSA-443-0525-GROUP1-FACEBOOK":
		case "MCSA-444-0525-GROUP2-FACEBOOK":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_0wePEp9RtDM2cqW";

		case "MCSA-445-0525-GROUP1-YOUTUBE":
		case "MCSA-446-0525-GROUP2-YOUTUBE":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_eXLvfATsQAFHi2a";

		case "MCSA-447-0525-GROUP1-TIKTOK":
		case "MCSA-448-0525-GROUP2-TIKTOK":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_eRNb3Zd0MwcByGW";

		case "MCSA-449-0525-GROUP1-INSTAGRAM":
		case "MCSA-450-0525-GROUP2-INSTAGRAM":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_55belTzuuuuKnRA";

		case "MCSA-451-0525-GROUP1-YAHOO":
		case "MCSA-452-0525-GROUP2-YAHOO":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_4JlfWzvupG3hIhg";

		default:
			assertNever(groupId);
	}
}

export const addMSCA0525CollConfig = (
	config: MCSA0525CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {
	  const FINISH_MESSAGE = "Congratulations! You have now completed all required tasks and your data has been uploaded. Your incentive has been processed. Thank you for your time.";
    const langStrings = getLanguageStrings(AppLanguage.en);

		const webPlatformId = getWebPlatformId(config.groupId);
		const webPlatformTask = generateWebPlatformTask({
        groupId: config.groupId,
        platform: getWebPlatform(config.groupId),
        webPlatformConfigUrl: getWebPlatformConfigUrl(webPlatformId),
        webPlatformID: getWebPlatformId(config.groupId),
        langStrings: langStrings,
        taskLengthMinutes: getTaskLengthMinutes(webPlatformId),
        useWebPlatformIDForTaskID: true
    });

    const externalSurveyTask = generateExternalSurveyTask({
        title: langStrings.Common.survey,
        description: langStrings.Survey.description,
        extraMetaFields: config.metaFieldNames,
        taskID: `${config.groupId}-SURVEY`,
        surveyUrl: getSurveyUrl(config.groupId)
    });

		const uploadTask = generateUploadsTask({
        config: {
            title: "Uploads"
        },
        taskId: `${config.groupId}-UPLOADS`
    });

    builder
        .setFlag('deviceLock', 'mobile')
        .setFlag('requireMinimumBattery', 0.3)
        .setFlag('preventTaskProgress', false)
        .setFlag('finishMessageOverride', FINISH_MESSAGE)
        .addTask(webPlatformTask)
        .addTask(externalSurveyTask)
        .addTask(uploadTask)

    return builder;
}